/**
 * Identity Authentication Client
 * Handles authentication with Welbee Identity App via HTTP-only cookies
 * Cross-domain session management for all Welbee applications
 */

import { getIdentityUrl as identityUrlForCurrentHost } from '../utils/config';
import { UserActivityTracker } from './UserActivityTracker';

const SESSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const SESSION_LOSS_RECHECK_DELAY_MS = 2 * 1000;
const TRANSIENT_FAILURE_BACKOFF_MS = 15 * 1000;
const TOKEN_REQUEST_TIMEOUT_MS = 10 * 1000;
const TOKEN_EXPIRY_LEEWAY_MS = 30 * 1000;
const WAKE_CHECK_THROTTLE_MS = 10 * 1000;
// Room for both requests of a 401 re-check plus the gap between them; a check that outlives it counts as transient.
const SESSION_CHECK_DEADLINE_MS = 2 * TOKEN_REQUEST_TIMEOUT_MS + SESSION_LOSS_RECHECK_DELAY_MS + 1000;
const LOGIN_REDIRECT_COOLDOWN_MS = 5 * 1000;
const LOGIN_REDIRECT_STORAGE_KEY = 'welbee_last_identity_redirect_ts';
// Identity must hear about activity at least this often, even in a tab whose polling has stalled.
const ACTIVITY_REPORT_MAX_AGE_MS = 5 * 60 * 1000;
const IDLE_SIGN_OUT_REASON = 'idle';

export const SessionStatus = Object.freeze({
  Active: 'active',
  Unavailable: 'unavailable',
  SignedOut: 'signed-out',
});

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isUsableToken = (body) =>
  typeof body?.jwtToken === 'string' && body.jwtToken.length > 0 && Number.isFinite(body.expiresAt);

export const withIdleMs = (url, idleMs) => {
  const [path, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  params.set('idleMs', String(idleMs));
  return `${path}?${params}`;
};

const readBodyIfAny = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export class IdentityAuth {
  constructor({ activityTracker = new UserActivityTracker() } = {}) {
    this.activityTracker = activityTracker;
    this.lastActivityReportAt = 0;
    this.lastActivityHeartbeatAt = 0;
    this.jwtToken = null;
    this.expiresAt = 0;
    this.sessionCheckInterval = null;
    this.scheduledCheck = null;
    this.inFlightCheck = null;
    this.transientBackoffUntil = 0;
    this.lastWakeCheckAt = 0;
    this.sessionListeners = new Set();
    this.signedOut = false;
  }

  /**
   * Get the Identity URL from config
   * This ensures we always get the current value, not a cached one
   */
  getIdentityUrl() {
    const identityUrl = identityUrlForCurrentHost();
    if (!identityUrl) {
      console.error('ERROR: REACT_APP_IDENTITY_URL is not set in environment variables.');
      console.error('Please ensure .env file exists with REACT_APP_IDENTITY_URL configured.');
      console.error('After creating/updating .env file, restart the development server.');
    }
    return identityUrl;
  }

  /**
   * Check authentication status with Identity app.
   * Resolves true while a token is held: a transient Identity failure keeps the
   * last good token instead of signing the user out.
   */
  async checkAuth() {
    await this.checkSession();
    return this.hasToken();
  }

  /**
   * Concurrent callers share one in-flight check, so parallel calls can never
   * be mistaken for repeated 401s.
   */
  checkSession() {
    if (this.signedOut) return Promise.resolve(SessionStatus.SignedOut);
    if (!this.inFlightCheck) {
      this.inFlightCheck = this.querySessionWithinDeadline().finally(() => {
        this.inFlightCheck = null;
      });
    }
    return this.inFlightCheck;
  }

  /**
   * Notifies the listener when a token appears or the session is confirmed gone,
   * including outcomes of checks nobody is awaiting. Returns the unsubscribe function.
   */
  subscribe(listener) {
    this.sessionListeners.add(listener);
    return () => {
      this.sessionListeners.delete(listener);
    };
  }

  publish(status) {
    this.sessionListeners.forEach((listener) => listener(status));
  }

  /**
   * Resolves true once a token is in hand, or false once Identity confirms the
   * session is gone. Transient failures are retried, never treated as sign-out.
   */
  async establishSession() {
    while ((await this.checkSession()) === SessionStatus.Unavailable && !this.hasToken()) {
      await delay(TRANSIENT_FAILURE_BACKOFF_MS);
    }
    return this.hasToken();
  }

  /**
   * A check that outlives its deadline is abandoned: its verdict may land after a
   * newer check has already settled the session, so it must not be applied.
   */
  querySessionWithinDeadline() {
    const check = { abandoned: false };
    let deadline;
    const deadlinePassed = new Promise((resolve) => {
      deadline = setTimeout(() => {
        check.abandoned = true;
        resolve(this.rideOutTransientFailure('session check exceeded its deadline'));
      }, SESSION_CHECK_DEADLINE_MS);
    });
    return Promise.race([this.querySession(check), deadlinePassed]).finally(() => clearTimeout(deadline));
  }

  async querySession(check) {
    const verdict = await this.readSessionVerdict();
    if (check.abandoned) return SessionStatus.Unavailable;
    return this.applyVerdict(verdict);
  }

  async readSessionVerdict() {
    try {
      let response = await this.requestToken();
      if (response.status === 401) {
        // A lone 401 can be a race (e.g. another tab mid-refresh); only a repeat confirms the session is gone.
        await delay(SESSION_LOSS_RECHECK_DELAY_MS);
        response = await this.requestToken();
      }

      if (response.status === 401) return { status: SessionStatus.SignedOut, reason: response.body?.reason };
      if (!response.ok) return { status: SessionStatus.Unavailable, reason: `HTTP ${response.status}` };
      if (!isUsableToken(response.body)) {
        return { status: SessionStatus.Unavailable, reason: 'token response without a usable token' };
      }
      return { status: SessionStatus.Active, token: response.body };
    } catch (error) {
      return { status: SessionStatus.Unavailable, reason: error };
    }
  }

  applyVerdict(verdict) {
    switch (verdict.status) {
      case SessionStatus.Active:
        return this.acceptToken(verdict.token);
      case SessionStatus.SignedOut:
        return this.endSession(verdict.reason);
      default:
        return this.rideOutTransientFailure(verdict.reason);
    }
  }

  /**
   * The body is read inside the timeout too: a body that stalls after the headers
   * arrive would otherwise hold the shared check open indefinitely.
   */
  async requestToken() {
    const abortController = new AbortController();
    const timeout = setTimeout(() => abortController.abort(), TOKEN_REQUEST_TIMEOUT_MS);
    try {
      const tokenUrl = withIdleMs(`${this.getIdentityUrl()}/api/auth/token-admin`, this.activityTracker.idleMs());
      const response = await fetch(tokenUrl, {
        credentials: 'include',
        mode: 'cors', // Explicitly set CORS mode
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });
      if (response.ok) return { status: response.status, ok: true, body: await response.json() };

      // A 401 body says why the session ended (e.g. idle); other failures carry nothing useful.
      const body = response.status === 401 ? await readBodyIfAny(response) : null;
      return { status: response.status, ok: false, body };
    } finally {
      clearTimeout(timeout);
    }
  }

  acceptToken({ jwtToken, expiresAt }) {
    if (this.signedOut) return SessionStatus.SignedOut;

    const tokenWasMissing = !this.hasToken();
    this.lastActivityReportAt = Date.now();
    this.jwtToken = jwtToken;
    this.expiresAt = expiresAt;
    this.transientBackoffUntil = 0;
    this.cancelScheduledCheck();
    if (tokenWasMissing) this.publish(SessionStatus.Active);
    return SessionStatus.Active;
  }

  rideOutTransientFailure(reason) {
    if (this.signedOut) return SessionStatus.SignedOut;

    console.warn('IdentityAuth: session check failed; keeping the current token and retrying shortly', reason);
    this.transientBackoffUntil = Date.now() + TRANSIENT_FAILURE_BACKOFF_MS;
    this.scheduleCheck(TRANSIENT_FAILURE_BACKOFF_MS);
    return SessionStatus.Unavailable;
  }

  endSession(reason) {
    if (this.signedOut) return SessionStatus.SignedOut;

    this.jwtToken = null;
    this.expiresAt = 0;
    this.redirectToLogin(reason);
    this.publish(SessionStatus.SignedOut);
    return SessionStatus.SignedOut;
  }

  /**
   * The cooldown slows a redirect loop between this app and Identity to one round
   * per cooldown. A held-back redirect re-checks once the cooldown lapses so the tab
   * is never left stranded.
   */
  redirectToLogin(reason) {
    const cooldownRemainingMs = this.loginRedirectCooldownRemainingMs();
    if (cooldownRemainingMs > 0) {
      this.scheduleCheck(cooldownRemainingMs);
      return;
    }

    this.markLoginRedirect();
    const loginUrl = `${this.getIdentityUrl()}/login`;
    window.location.href = reason === IDLE_SIGN_OUT_REASON ? `${loginUrl}?reason=${IDLE_SIGN_OUT_REASON}` : loginUrl;
  }

  loginRedirectCooldownRemainingMs() {
    try {
      const lastRedirectAt = Number(localStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY));
      return Math.max(0, lastRedirectAt + LOGIN_REDIRECT_COOLDOWN_MS - Date.now());
    } catch {
      return 0;
    }
  }

  markLoginRedirect() {
    try {
      localStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, Date.now().toString());
    } catch {
      // Storage can be unavailable (private mode, quota); the redirect must still happen.
    }
  }

  scheduleCheck(delayMs) {
    if (this.signedOut) return;

    this.cancelScheduledCheck();
    this.scheduledCheck = setTimeout(() => {
      this.scheduledCheck = null;
      void this.checkSession();
    }, delayMs);
  }

  cancelScheduledCheck() {
    if (this.scheduledCheck) {
      clearTimeout(this.scheduledCheck);
      this.scheduledCheck = null;
    }
  }

  /**
   * Start periodic session checks, plus an immediate re-check whenever the page
   * becomes usable again: timers stall in background tabs and while the device sleeps.
   * Failed checks never stop polling; only stopSessionCheck() does.
   */
  startSessionCheck() {
    if (this.signedOut || this.sessionCheckInterval) return;

    this.sessionCheckInterval = setInterval(() => void this.checkSession(), SESSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('pageshow', this.handlePageShow);
    // After the wake listeners, so a wake check still reports how long the user was away.
    this.activityTracker.start(this.handleUserActivity);
  }

  /**
   * Stop periodic session checks
   */
  stopSessionCheck() {
    this.cancelScheduledCheck();
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.activityTracker.stop();
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.checkSessionOnWake();
  };

  handleOnline = () => this.checkSessionOnWake();

  handlePageShow = (event) => {
    if (event.persisted) this.checkSessionOnWake();
  };

  /**
   * Polling can stall (background tabs, sleep), so an active user whose activity Identity
   * has not heard about recently gets a check straight away, at most once per report window.
   */
  handleUserActivity = () => {
    if (this.signedOut || !this.hasToken()) return;

    const now = Date.now();
    if (now - this.lastActivityReportAt < ACTIVITY_REPORT_MAX_AGE_MS) return;
    if (now - this.lastActivityHeartbeatAt < ACTIVITY_REPORT_MAX_AGE_MS) return;

    this.lastActivityHeartbeatAt = now;
    void this.checkSession();
  };

  checkSessionOnWake() {
    const now = Date.now();
    if (now - this.lastWakeCheckAt < WAKE_CHECK_THROTTLE_MS) return;

    this.lastWakeCheckAt = now;
    void this.checkSession();
  }

  /**
   * Get current JWT token
   */
  getToken() {
    return this.jwtToken;
  }

  /**
   * The token to attach to an API request, refreshed first when it has expired or
   * is about to. While backing off after a failed check, the last good token is used as-is.
   */
  async getValidToken() {
    if (this.isTokenExpiring() && !this.isBackingOff()) {
      await this.checkSession();
    }
    return this.jwtToken;
  }

  isTokenExpiring() {
    return Date.now() >= this.expiresAt - TOKEN_EXPIRY_LEEWAY_MS;
  }

  isBackingOff() {
    return Date.now() < this.transientBackoffUntil;
  }

  hasToken() {
    return Boolean(this.jwtToken);
  }

  /**
   * Manually refresh the token
   * Useful for proactive token refresh before making important API calls
   */
  async refreshToken() {
    return await this.checkAuth();
  }
  
  /**
   * Decode JWT token and return claims
   */
  getClaims() {
    if (!this.jwtToken) return null;
    try {
      const base64 = this.jwtToken
        .split('.')[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      return JSON.parse(
        decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
      );
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    const claims = this.getClaims();
    if (!claims || !claims.claims || !claims.claims.welbee_roles) return false;
    
    return claims.claims.welbee_roles.some(userRole =>
      userRole.toLowerCase().includes(role.toLowerCase())
    );
  }

  /**
   * Get user information from JWT claims
   * Simplified for super admin - basic user info and access control
   */
  getUserInfo() {
    const claims = this.getClaims();
    if (!claims || !claims.claims) return null;
    
    const welbeeClaims = claims.claims;
    const accessControl = welbeeClaims.welbee_access_control || {};
    
    // Extract groups and schools from access control
    const groups = accessControl.groups || [];
    const standaloneSchools = accessControl.standalone_school_ids || [];
    
    // Get all school IDs from groups and standalone schools
    const allSchoolIds = [
      ...groups.flatMap(group => group.school_ids || []),
      ...standaloneSchools
    ];
    
    // Determine if user is MAT (has groups)
    const isMat = groups.length > 0;
    
    // Get primary group ID (first group)
    const primaryGroupId = groups.length > 0 ? groups[0].group_id : null;
    
    // Get primary school ID (first school)
    const primarySchoolId = allSchoolIds.length > 0 ? allSchoolIds[0] : null;
    
    return {
      // Basic user info
      userId: welbeeClaims.welbee_internal_user_id || '',
      email: welbeeClaims.welbee_email || '',
      name: welbeeClaims.welbee_name || '',
      roles: welbeeClaims.welbee_roles || [],
      userType: welbeeClaims.welbee_user_type || '',
      
      // Organization info (basic)
      groups: groups.map(group => ({
        id: group.group_id,
        allSchools: group.all_schools,
        schoolIds: group.school_ids || []
      })),
      schools: allSchoolIds,
      isMat: isMat,
      matGroupId: primaryGroupId,
      schoolId: primarySchoolId,
      
      // Access control info
      accessControl: {
        groups: groups,
        standaloneSchoolIds: standaloneSchools,
        accessOneSchoolOnly: accessControl.access_one_school_only || false,
        accessMultipleCustomers: accessControl.access_multiple_customers || false
      },
      
      // Token metadata
      claimsVersion: welbeeClaims.welbee_claims_version || '1.0',
      issuedAt: welbeeClaims.welbee_issued_at,
      expiresAt: welbeeClaims.welbee_expires_at
    };
  }

  /**
   * Check if current session is expired
   */
  isSessionExpired() {
    if (!this.expiresAt) return true;
    return Date.now() >= this.expiresAt;
  }

  /**
   * Check if user is a MAT (Multi-Academy Trust) user
   */
  isMatUser() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.isMat : false;
  }

  /**
   * Get user's primary group ID
   */
  getPrimaryGroupId() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.matGroupId : null;
  }

  /**
   * Get user's primary school ID
   */
  getPrimarySchoolId() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.schoolId : null;
  }

  /**
   * Get all school IDs user has access to
   */
  getAllSchoolIds() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.schools : [];
  }

  /**
   * Check if user has access to a specific school
   */
  hasAccessToSchool(schoolId) {
    const schoolIds = this.getAllSchoolIds();
    return schoolIds.includes(schoolId);
  }

  /**
   * Check if user has access to all schools in a group
   */
  hasAccessToAllSchools() {
    const userInfo = this.getUserInfo();
    if (!userInfo || !userInfo.groups.length) return false;
    
    return userInfo.groups.some(group => group.allSchools);
  }

  /**
   * Logout user and redirect to Identity app
   */
  async logout() {
    this.signedOut = true;
    this.stopSessionCheck();
    this.jwtToken = null;
    this.expiresAt = 0;

    const identityUrl = this.getIdentityUrl();
    try {
      await fetch(`${identityUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // The redirect below still ends the session in this tab.
    }
    window.location.href = `${identityUrl}/login`;
  }

  /**
   * Make authenticated API calls with JWT token
   */
  async fetchWithAuth(url, options = {}) {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    return fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  /**
   * Impersonate as another user for a specific product context
   * @param {Object} payload - Impersonation payload
   * @param {Object} payload.accessTarget - Target entity information
   * @param {Object} payload.schoolAccess - School access scope
   * @param {string} payload.callbackUrl - Callback URL after impersonation
   * @param {number} [payload.productType] - Optional product type identifier
   * @param {string} [payload.email] - Optional email address for impersonation context
   */
  async impersonate(payload) {
    const identityUrl = this.getIdentityUrl();
    // Try to read non-HttpOnly cookies if available; if HttpOnly, this returns null and that's fine
    const getCookieValue = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts.pop().split(';').shift();
      }
      return null;
    };
    const sessionId = getCookieValue('welbee_auth_admin');
    
    // Note: welbee_auth_admin is HttpOnly, so it cannot be read by JavaScript
    // We rely on credentials: 'include' to automatically send HttpOnly cookies
    const headers = {
      'Content-Type': 'application/json',
    };
    if (sessionId) {
      headers['x-session-id'] = sessionId;
    }

    const requestPayload =
      payload && typeof payload === 'object'
        ? (() => {
            const normalizedProductType =
              payload.productType == null
                ? null
                : Number(payload.productType);
            const sanitizedEmail =
              payload.email == null ? '' : String(payload.email).trim();
            const shouldIncludeEmail =
              normalizedProductType === 1 && sanitizedEmail.length > 0;

            return {
              ...payload,
              ...(normalizedProductType == null
                ? {}
                : { productType: normalizedProductType }),
              ...(shouldIncludeEmail ? { email: sanitizedEmail } : {}),
            };
          })()
        : payload;

    return await fetch(`${identityUrl}/api/auth/impersonate`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(requestPayload),
    });
  }
}

export const identityAuth = new IdentityAuth();
